import { useLayoutEffect, useRef } from "preact/hooks";
import { UnlockKind } from "../../../api/types";
import type { ProgressionInfo } from "../../../api/types";
import type { MetaIndex } from "../../../state/meta";
import { LockBadge } from "../../components/Badge";
import { cx } from "../../cx";
import { roadmapNodes } from "./profileStats";
import type { RoadmapNode } from "./profileStats";

type UnlockNames = Record<UnlockKind, ReadonlyMap<string, { name: string }>>;

const KIND_LABEL: Record<UnlockKind, string> = {
  [UnlockKind.WEAPON]: "Weapon",
  [UnlockKind.PERK]: "Perk",
  [UnlockKind.EQUIPMENT]: "Equipment",
};

export interface RoadmapProps {
  playerLevel: number;
  progression: ProgressionInfo;
  index: MetaIndex;
}

function CheckIcon() {
  return (
    <svg class="profile__check" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path d="M3 8.5 6.4 12 13 4.5" fill="none" stroke="currentColor" stroke-width="2" />
    </svg>
  );
}

function RoadmapNodeView({ node, names }: { node: RoadmapNode; names: UnlockNames }) {
  return (
    <li class={cx("profile__node", `is-${node.state}`)} aria-current={node.state === "current" ? "step" : undefined}>
      <span class="profile__dot" aria-hidden="true" />
      <div class="profile__node-head">
        {node.state === "locked" ? (
          <>
            <LockBadge level={node.level} />
            <span class="sr-only">Locked</span>
          </>
        ) : (
          <>
            <span class="profile__node-level">
              <span class="eyebrow">Lvl</span>
              <span class="lcd" data-ghost="88">
                {node.level}
              </span>
            </span>
            {node.state === "current" ? (
              <span class="profile__now">Current</span>
            ) : (
              <>
                <CheckIcon />
                <span class="sr-only">Reached</span>
              </>
            )}
          </>
        )}
      </div>
      {node.items.length > 0 ? (
        <ul class="profile__unlocks">
          {node.items.map((item) => (
            <li key={`${item.kind}:${item.id}`}>
              <span class="profile__kind">{KIND_LABEL[item.kind]}</span>
              <span class="profile__unlock-name">{names[item.kind].get(item.id)?.name ?? item.id}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p class="profile__node-empty muted">No new unlocks at this level</p>
      )}
    </li>
  );
}

/** Level timeline of everything the catalog unlocks, centred on the player's current level. */
export function Roadmap({ playerLevel, progression, index }: RoadmapProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const nodes = roadmapNodes(progression.unlocks, playerLevel);
  const names: UnlockNames = {
    [UnlockKind.WEAPON]: index.weapons,
    [UnlockKind.PERK]: index.perks,
    [UnlockKind.EQUIPMENT]: index.equipment,
  };

  // Position the list itself (never scrollIntoView: that would also scroll the frame body and the page).
  useLayoutEffect(() => {
    const box = scroller.current;
    const current = box?.querySelector<HTMLElement>('[aria-current="step"]');
    if (!box || !current) return;
    box.scrollTop = current.offsetTop - (box.clientHeight - current.offsetHeight) / 2;
  }, [playerLevel]);

  return (
    <section class="profile__panel" aria-labelledby="profile-roadmap-title">
      <header class="profile__panel-head">
        <h2 id="profile-roadmap-title" class="profile__h">
          Unlock roadmap
        </h2>
        <span class="eyebrow num">
          Level {playerLevel} of {progression.maxLevel}
        </span>
      </header>
      <div class="profile__scroll" tabIndex={0} role="region" aria-labelledby="profile-roadmap-title" ref={scroller}>
        <ol class="profile__roadmap">
          {nodes.map((node) => (
            <RoadmapNodeView key={node.level} node={node} names={names} />
          ))}
        </ol>
      </div>
    </section>
  );
}
